/*!
 * Almagrove Jewellers — Shopify theme
 * Handcrafted by Sector 7 · https://sector7hq.co
 * © 2026 Sector7HQ Ltd. All rights reserved.
 */
/**
 * Almagrove header — desktop mega-menu + mobile drawer
 *
 * Migrated from the inline IIFE in sections/almagrove-header.liquid per the
 * Almagrove JS pattern (see assets/alm-wishlist.js header for the checklist).
 *
 * Behaviour
 * ---------
 * Desktop (≥ 990px):
 *   - Each top-level nav item with children renders a button + panel.
 *   - Hover-intent: panel opens after a short delay; closing also delayed so
 *     the cursor can travel from button → panel without flicker.
 *   - Click toggles instantly (touch + keyboard).
 *   - Only one panel open at a time.
 *   - ESC closes; click outside the header closes; focus on a non-header
 *     element closes.
 *   - Keyboard:
 *       Enter / Space on trigger → toggle
 *       ESC → close + return focus to trigger
 *       Tab from inside panel exits naturally (browser handles).
 *
 * Mobile (< 990px):
 *   - Hamburger toggles the drawer.
 *   - Each parent item is an accordion: button toggles its sub-panel.
 *   - Body scroll is locked while the drawer is open.
 *   - ESC closes drawer.
 *
 * Re-hydrates on `shopify:section:load` so Theme Editor edits work.
 */
(function () {
  'use strict';

  var DESKTOP_BP = 990;
  var OPEN_DELAY_MS = 120;
  var CLOSE_DELAY_MS = 220;

  function isDesktop() {
    return window.matchMedia('(min-width: ' + DESKTOP_BP + 'px)').matches;
  }

  // ----- Desktop mega menu --------------------------------------------------

  function initDesktopNav(root) {
    var nav = root.querySelector('[data-alm-nav]');
    if (!nav) return;
    var items = nav.querySelectorAll('.alm-header__nav-item--has-panel');
    if (!items.length) return;

    var openTimer = null;
    var closeTimer = null;
    var openItem = null;

    function clearTimers() {
      if (openTimer) {
        clearTimeout(openTimer);
        openTimer = null;
      }
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
    }

    function openPanel(item) {
      if (openItem && openItem !== item) closePanel(openItem, true);
      var trigger = item.querySelector('[data-alm-nav-trigger]');
      var panel = item.querySelector('[data-alm-nav-panel]');
      if (!trigger || !panel) return;
      trigger.setAttribute('aria-expanded', 'true');
      panel.removeAttribute('hidden');
      item.classList.add('is-open');
      openItem = item;
      root.classList.add('alm-header--panel-open');
    }

    function closePanel(item, immediate) {
      var trigger = item.querySelector('[data-alm-nav-trigger]');
      var panel = item.querySelector('[data-alm-nav-panel]');
      if (!trigger || !panel) return;
      trigger.setAttribute('aria-expanded', 'false');
      panel.setAttribute('hidden', '');
      item.classList.remove('is-open');
      if (openItem === item) openItem = null;
      if (!openItem) root.classList.remove('alm-header--panel-open');
    }

    function closeAll() {
      clearTimers();
      items.forEach(function (item) {
        closePanel(item, true);
      });
    }

    items.forEach(function (item) {
      var trigger = item.querySelector('[data-alm-nav-trigger]');
      var panel = item.querySelector('[data-alm-nav-panel]');
      if (!trigger || !panel) return;

      // Hover-intent open
      item.addEventListener('mouseenter', function () {
        if (!isDesktop()) return;
        clearTimers();
        openTimer = setTimeout(function () {
          openPanel(item);
        }, OPEN_DELAY_MS);
      });

      item.addEventListener('mouseleave', function () {
        if (!isDesktop()) return;
        clearTimers();
        closeTimer = setTimeout(function () {
          closePanel(item);
        }, CLOSE_DELAY_MS);
      });

      // Click toggles (works for touch + keyboard activation)
      // trigger.addEventListener('click', function (ev) {
      //   ev.preventDefault();
      //   if (!isDesktop()) return;
      //   var expanded = trigger.getAttribute('aria-expanded') === 'true';
      //   clearTimers();
      //   if (expanded) {
      //     closePanel(item);
      //   } else {
      //     openPanel(item);
      //   }
      // });

      // ESC closes from anywhere within
      item.addEventListener('keydown', function (ev) {
        if (ev.key === 'Escape' || ev.key === 'Esc') {
          if (item.classList.contains('is-open')) {
            ev.stopPropagation();
            closePanel(item);
            trigger.focus();
          }
        }
      });
    });

    // Click outside header → close
    document.addEventListener('click', function (ev) {
      if (!openItem) return;
      if (!root.contains(ev.target)) closePanel(openItem);
    });

    // Focus moving outside the header → close (keyboard tab-out)
    document.addEventListener('focusin', function (ev) {
      if (!openItem) return;
      if (!root.contains(ev.target)) closePanel(openItem);
    });

    // Cleanup hooks for re-init
    root.__almNavCleanup = closeAll;
  }

  // ----- Mobile drawer ------------------------------------------------------

  function initMobileDrawer(root) {
    var sectionId = root.getAttribute('data-section-id');
    var toggle = root.querySelector('[data-alm-nav-toggle]');
    var drawerId = toggle && toggle.getAttribute('aria-controls');
    var drawer = drawerId ? document.getElementById(drawerId) : null;
    if (!toggle || !drawer) return;

    function setOpen(open) {
      toggle.setAttribute('aria-expanded', String(open));
      if (open) {
        drawer.removeAttribute('hidden');
        document.documentElement.classList.add('alm-mobile-nav-open');
      } else {
        drawer.setAttribute('hidden', '');
        document.documentElement.classList.remove('alm-mobile-nav-open');
      }
    }

    toggle.addEventListener('click', function () {
      var expanded = toggle.getAttribute('aria-expanded') === 'true';
      setOpen(!expanded);
    });

    // ESC closes the drawer
    document.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Escape' && ev.key !== 'Esc') return;
      if (toggle.getAttribute('aria-expanded') === 'true') {
        setOpen(false);
        toggle.focus();
      }
    });

    // Mobile sub-accordions
    var triggers = drawer.querySelectorAll('[data-alm-mobile-trigger]');
    triggers.forEach(function (trigger) {
      var panelId = trigger.getAttribute('aria-controls');
      var panel = panelId ? document.getElementById(panelId) : null;
      if (!panel) return;
      trigger.addEventListener('click', function () {
        var expanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!expanded));
        if (expanded) {
          panel.setAttribute('hidden', '');
          trigger.parentElement.classList.remove('is-open');
        } else {
          panel.removeAttribute('hidden');
          trigger.parentElement.classList.add('is-open');
        }
      });
    });

    // Reset state when crossing breakpoint (avoids stuck-open desktop panels)
    var mq = window.matchMedia('(min-width: ' + DESKTOP_BP + 'px)');
    var onChange = function () {
      // Close mobile drawer on resize-up
      if (mq.matches && toggle.getAttribute('aria-expanded') === 'true') {
        setOpen(false);
      }
    };
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
    } else if (mq.addListener) {
      mq.addListener(onChange);
    }
  }

  // ----- Boot ---------------------------------------------------------------

  // ----- Sticky / condensed scroll behaviour --------------------------------

  function initStickyScroll(root) {
    var threshold = 60; // px scrolled before condensing
    var lastY = window.scrollY;
    var ticking = false;

    function update() {
      ticking = false;
      var y = window.scrollY;
      // Toggle "scrolled" any time we're past zero (just for the shadow)
      root.classList.toggle('is-scrolled', y > 4);
      // Toggle "condensed" past the threshold; restore when scrolled near top
      if (y > threshold) root.classList.add('is-condensed');
      else if (y < threshold - 20) root.classList.remove('is-condensed');
      lastY = y;
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  // ----- Predictive search dropdown -----------------------------------------

  function initPredictiveSearch(root) {
    var form = root.querySelector('.alm-search-form');
    if (!form) return;
    var input = form.querySelector('input[type="search"]');
    if (!input) return;

    // Build a results panel attached to the form. CSS positions it absolutely.
    var panel = document.createElement('div');
    panel.className = 'alm-search-results';
    panel.setAttribute('role', 'listbox');
    panel.setAttribute('aria-label', 'Search suggestions');
    panel.hidden = true;
    form.appendChild(panel);

    var debounceTimer = null;
    var lastQuery = '';
    var inflight = null;

    function close() {
      panel.hidden = true;
      panel.innerHTML = '';
    }

    function escape(s) {
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    function formatMoney(cents) {
      var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
      try {
        return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency });
      } catch (e) {
        return '£' + (cents / 100).toFixed(2);
      }
    }

    function renderEmpty(q) {
      panel.innerHTML =
        '<div class="alm-search-results__empty">' +
          '<p>No matches for <strong>' + escape(q) + '</strong>.</p>' +
          '<a href="/search?q=' + encodeURIComponent(q) + '" class="alm-search-results__view-all">See all results</a>' +
        '</div>';
      panel.hidden = false;
    }

    function render(data, q) {
      var resources = (data && data.resources && data.resources.results) || {};
      var products = resources.products || [];
      var collections = resources.collections || [];
      var pages = resources.pages || [];

      if (products.length === 0 && collections.length === 0 && pages.length === 0) {
        renderEmpty(q);
        return;
      }

      var html = '';

      if (products.length) {
        html += '<section class="alm-search-results__group">';
        html += '<h3 class="alm-search-results__heading">Products</h3>';
        html += '<ul role="list" class="alm-search-results__list">';
        products.slice(0, 5).forEach(function (p) {
          var img = p.image ? '<img src="' + p.image + '" alt="" loading="lazy">' : '';
          var price = p.price ? '<span class="alm-search-results__price">' + formatMoney(p.price * 100) + '</span>' : '';
          html += '<li><a href="' + p.url + '" class="alm-search-results__product">' +
                    '<span class="alm-search-results__thumb">' + img + '</span>' +
                    '<span class="alm-search-results__meta">' +
                      '<span class="alm-search-results__title">' + escape(p.title) + '</span>' +
                      price +
                    '</span>' +
                  '</a></li>';
        });
        html += '</ul></section>';
      }

      if (collections.length) {
        html += '<section class="alm-search-results__group">';
        html += '<h3 class="alm-search-results__heading">Collections</h3>';
        html += '<ul role="list" class="alm-search-results__list">';
        collections.slice(0, 3).forEach(function (c) {
          html += '<li><a href="' + c.url + '" class="alm-search-results__row">' + escape(c.title) + '</a></li>';
        });
        html += '</ul></section>';
      }

      if (pages.length) {
        html += '<section class="alm-search-results__group">';
        html += '<h3 class="alm-search-results__heading">Pages</h3>';
        html += '<ul role="list" class="alm-search-results__list">';
        pages.slice(0, 3).forEach(function (pg) {
          html += '<li><a href="' + pg.url + '" class="alm-search-results__row">' + escape(pg.title) + '</a></li>';
        });
        html += '</ul></section>';
      }

      html += '<a href="/search?q=' + encodeURIComponent(q) + '" class="alm-search-results__view-all">See all results for "' + escape(q) + '"</a>';

      panel.innerHTML = html;
      panel.hidden = false;
    }

    function fetchSuggestions(q) {
      if (inflight && inflight.abort) inflight.abort();
      var ctl = new AbortController();
      inflight = ctl;
      var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
                '&resources[type]=product,collection,page&resources[limit]=8&resources[options][unavailable_products]=last';
      return fetch(url, { signal: ctl.signal })
        .then(function (r) { return r.json(); })
        .then(function (data) { render(data, q); })
        .catch(function () { /* aborted or network error — silent */ });
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(debounceTimer);
      if (q.length < 2) {
        close();
        return;
      }
      if (q === lastQuery) return;
      lastQuery = q;
      debounceTimer = setTimeout(function () { fetchSuggestions(q); }, 220);
    });

    input.addEventListener('focus', function () {
      if (lastQuery && input.value.trim().length >= 2) panel.hidden = false;
    });

    document.addEventListener('click', function (ev) {
      if (!form.contains(ev.target)) close();
    });

    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape' && !panel.hidden) {
        close();
        input.focus();
      }
    });
  }

  // ----- Boot ---------------------------------------------------------------

  function initHeader(root) {
    if (!root) return;
    if (root.__almHeaderInit) return;
    root.__almHeaderInit = true;
    initDesktopNav(root);
    initMobileDrawer(root);
    initStickyScroll(root);
    initPredictiveSearch(root);
  }

  function initAll() {
    document.querySelectorAll('[data-alm-header]').forEach(initHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-hydrate after Theme Editor section edits
  document.addEventListener('shopify:section:load', function (ev) {
    var root = ev.target.querySelector('[data-alm-header]');
    if (root) {
      root.__almHeaderInit = false;
      initHeader(root);
    }
  });
})();
